import React from 'react';
import { useFragment, useMutation } from 'react-relay/hooks';
import { graphql } from 'react-relay';
import classes from './TransactionItem.module.scss';
import dayjs from 'dayjs';
import { useAlert } from 'react-alert';
import { TransactionItem_transaction$key } from './__generated__/TransactionItem_transaction.graphql';
import Button from '../../atoms/Button/Button';
import { alertMessageTimeout } from '../AlertMessage/alertMessageTimeout';
import ClockIcon from '../../assets/clock.svg';
import Txt from '../../atoms/Txt/Txt';
import { TransactionItemResendGiftCardMutation$data } from './__generated__/TransactionItemResendGiftCardMutation.graphql';
import Notice from 'components/atoms/Notice/Notice';
import useRouterCountry from 'hooks/useRouterCountry';
import { URL_LOCALE_DATE_LOCALE_MAP } from '../../../utils/locale';

export interface Props {
  transactionRef: TransactionItem_transaction$key;
}

const fragmentSpec = graphql`
  fragment TransactionItem_transaction on Transaction {
    id
    createdAt
    eventType
    amount {
      formatted
    }
    giftCardId
    description
    status
  }
`;

const resendGiftCardMutation = graphql`
  mutation TransactionItemResendGiftCardMutation($input: ResendGiftCardInput!) {
    resendGiftCard(input: $input) {
      successful
      message
    }
  }
`;

const TransactionItem: React.FC<Props> = ({ transactionRef }) => {
  const transaction = useFragment(fragmentSpec, transactionRef);
  const userCountry = useRouterCountry();

  const [resendGiftCard, isResendingGiftCard] = useMutation<any>(resendGiftCardMutation);
  const alert = useAlert();

  function handleResendGiftCard() {
    resendGiftCard({
      variables: { input: { giftCardId: transaction.giftCardId } },
      onCompleted: (response: TransactionItemResendGiftCardMutation$data) => {
        alert.show(response.resendGiftCard?.message, {
          type: response.resendGiftCard?.successful ? 'success' : 'error',
          timeout: alertMessageTimeout(response.resendGiftCard?.message),
        });
      },
    });
  }

  function getCashoutType() {
    switch (transaction.description) {
      case 'charity':
        return 'Charity ';
      case 'gift_card':
        return 'Gift Card ';
      case 'paypal':
        return 'PayPal ';
      default:
        return '';
    }
  }

  return (
    <tr className={classes.transactionContainer}>
      <th className={classes.tableHeading}>
        <p className={classes.idText}>
          {dayjs(transaction.createdAt)
            .locale(URL_LOCALE_DATE_LOCALE_MAP[userCountry])
            .format('L - LT')}
        </p>
        <span className={classes.eventName}>
          <Txt>
            {transaction.eventType === 'Cashout' ? getCashoutType() : ''}
            {/* Convert transaction type in camelCase to human readable */}
            {transaction.eventType.replace(/([A-Z])/g, ' $1').trim()}
          </Txt>
          {transaction.status === 'Pending' && (
            <Notice status="info" className={classes.processingIndicator}>
              <ClockIcon />
              Processing
            </Notice>
          )}
        </span>
      </th>
      <td className={classes.dataWrapper}>
        <div className={classes.transactionData}>
          {transaction.giftCardId && (
            <Button
              disabled={isResendingGiftCard}
              onClick={handleResendGiftCard}
              color="accent"
              buttonType="tertiary"
            >
              <Txt component="p">Resend Gift Card</Txt>
            </Button>
          )}
          <p className={classes.pricingText}>{transaction.amount.formatted}</p>
        </div>
      </td>
    </tr>
  );
};

export default TransactionItem;
